import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Tagify, settings } from './tagify';
import { get } from 'helpers/api';
import useTrigger from 'hooks/use_trigger';

import './styles.scss';

// Tagify doesn't play nice with hooks in all cases, so users of this that use state usually have to be class components
// TODO: Can this be a class component with internal state so that consumers can use hooks?
export default function DealTaggedBodyInput({
  autoFocus,
  availableTags,
  availableTagsPath,
  body,
  bodyParts = [],
  placeholder,
  tooltipPosition,
  onChange,

  // TODO: Can we replace these three things with `onEnter`, and track dropdown state internally?
  onDropdownHide,
  onDropdownShow,
  onKeyDown,
}) {
  const className = `deal-tagged-body-input form-control ${getTooltipPositionClassName(tooltipPosition)}`;

  // Initialize the whitelist to the tags present in the body so Tagify can render them immediately
  const [whitelist, setWhitelist] = useState(bodyParts.filter(part => typeof part !== 'string'));

  useEffect(() => {
    (async () => {
      if (availableTagsPath) {
        availableTags = await get(availableTagsPath);
      } else {
        availableTags = availableTags || [];
      }

      // Since enforceWhitelist is true in our settings (to prevent users from defining new tags),
      // and because when that is true Tagify won't render tags that are not in the whitelist,
      // ensure that all tags from an existing body are whitelisted too
      const availableAndStaleTags = [...availableTags];
      const tagValues = new Set(availableTags.map(tag => tag.value));
      for (const part of bodyParts) {
        if (typeof part !== 'string' && !tagValues.has(part.value)) {
          availableAndStaleTags.push(part);
          tagValues.add(part.value);
        }
      }

      // Must sort, as just typing @ will sho the dropdown but not call the custom sort function,
      // and group header rendering depends on having an ordered list
      setWhitelist(settings.dropdown.sortby(availableAndStaleTags));
    })();
  }, []);

  // Tagify is uncontrolled and provides no interface for clearing the body - deal with that by
  // unmounting and remounting it when the body prop is cleared, using an fake Tagify element as a
  // placeholder to prevent things from jumping around on screen.
  const reset = useTrigger(!body);
  if (reset) {
    return <div className={`${className} tagify`} />;
  }

  // NOTE: Tagify is uncontrolled and acts oddly if the value prop is used instead of defaultValue
  return (
    <Tagify
      autoFocus={autoFocus}
      className={className}
      defaultValue={body}
      placeholder={placeholder}
      settings={settings}
      whitelist={whitelist}
      onChange={event => onChange(event.detail.value === '\n' ? '' : event.detail.value)}
      onDropdownHide={onDropdownHide}
      onDropdownShow={onDropdownShow}
      onKeydown={event => onKeyDown && onKeyDown(event.detail.event, event)}
    />
  );
}

DealTaggedBodyInput.propTypes = {
  autoFocus: PropTypes.bool,
  availableTags: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
  })),
  availableTagsPath: PropTypes.string,
  body: PropTypes.string.isRequired,
  bodyParts: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      value: PropTypes.string.isRequired,
    }),
  ])),
  placeholder: PropTypes.string,
  tooltipPosition: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onDropdownHide: PropTypes.func,
  onDropdownShow: PropTypes.func,
  onKeyDown: PropTypes.func,
};

function getTooltipPositionClassName(tooltipPosition) {
  const classNames = [];

  for (const position of ['top', 'left']) {
    if (tooltipPosition?.includes(position)) {
      classNames.push(`tooltips-${position}`);
    }
  }

  return classNames.join(' ');
}
